// Display formatters. Always render dates in the user's local zone.

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

// new Date("YYYY-MM-DD") parses as UTC *midnight*, so rendering it in any
// US timezone shifts the calendar date back a day. Date-only strings are
// calendar dates, not instants — construct them in local time instead.
function parseDateInput(iso: string): Date {
  const m = DATE_ONLY.exec(iso);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(iso);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = parseDateInput(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** next-due display values are heterogeneous: ISO dates, bare years, or
 * compliance-period ranges. Format real dates; pass the rest through. */
export function formatDueDisplay(value: string | null | undefined): string {
  if (!value) return "—";
  return DATE_ONLY.test(value) ? formatDate(value) : value;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatYYYYMM(yyyymm: string | null | undefined): string {
  if (!yyyymm || yyyymm.length !== 6) return "—";
  const year = Number.parseInt(yyyymm.slice(0, 4), 10);
  const month = Number.parseInt(yyyymm.slice(4), 10);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return "—";
  const d = new Date(Date.UTC(year, month - 1, 1));
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short" });
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString();
}

export function yearsAgo(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = parseDateInput(iso);
  if (Number.isNaN(d.getTime())) return null;
  return (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
}

export function monthsAgoFromYYYYMM(yyyymm: string | null | undefined): number | null {
  if (!yyyymm || yyyymm.length !== 6) return null;
  const y = Number.parseInt(yyyymm.slice(0, 4), 10);
  const m = Number.parseInt(yyyymm.slice(4), 10);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return null;
  const now = new Date();
  return (now.getFullYear() - y) * 12 + (now.getMonth() + 1 - m);
}

// "1 yr 3 mo ago", "2 mo ago", "12 d ago", "today" — coarse but readable.
// For dates in the future, returns "in <span>". Returns null for nullish input.
export function formatRelativeDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = parseDateInput(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const past = diffMs >= 0;
  const absDays = Math.floor(Math.abs(diffMs) / 86_400_000);
  const wrap = (s: string): string => (past ? `${s} ago` : `in ${s}`);
  if (absDays === 0) return "today";
  if (absDays < 30) return wrap(`${absDays} d`);
  const months = Math.floor(absDays / 30.44);
  if (months < 12) return wrap(`${months} mo`);
  const years = Math.floor(months / 12);
  const remMonths = months - years * 12;
  return wrap(remMonths === 0 ? `${years} yr` : `${years} yr ${remMonths} mo`);
}
